import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";

const EditStudent = () => {
  const { api, user } = useAuth();
  const { studentId } = useParams();
  const { t, i18n } = useTranslation("editstudent");
  const isRTL = i18n.language === 'ar';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    admission_number: '',
    gender: '',
    date_of_birth: '',
    date_of_joining: '',
    address: '',
    class_id: 0,
    stream_id: 0,
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (formData.class_id) {
      const classObj = classes.find(c => c.id === parseInt(formData.class_id));
      setSelectedClass(classObj);
    }
  }, [formData.class_id, classes]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch student details and classes in parallel
      const [studentResponse, classesResponse] = await Promise.all([
        api.get(`/api/v1/admin/schools/${user.school_reg}/students/${studentId}`),
        api.get(`/api/v1/admin/schools/${user.school_reg}/classes`)
      ]);

      setFormData(studentResponse.data);
      setClasses(classesResponse.data);
      
    } catch (error) {
      showNotification('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error) => {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'string') {
        return detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        return detail.map(err => err.msg).join(', ');
      }
    }
    return t('error');
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset stream_id when class changes
    if (name === 'class_id') {
      setFormData(prev => ({
        ...prev,
        stream_id: 0
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      const payload = {
        ...formData,
        class_id: parseInt(formData.class_id),
        stream_id: parseInt(formData.stream_id)
      };
      
      await api.patch(
        `/api/v1/admin/schools/${user.school_reg}/students/${studentId}`,
        payload
      );
      
      showNotification('success', t('success'));
      
    } catch (error) {
      showNotification('error', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E5252]"></div>
          <p className={`mt-4 text-gray-600 ${isRTL ? 'font-arabic' : ''}`}>
            {t('loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 ${isRTL ? 'rtl font-arabic' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-3xl mx-auto">
        {/* Notification */}
        {notification.message && (
          <div className={`mb-6 p-4 rounded-lg transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <svg className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span className="font-medium">{notification.message}</span>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          <div className="bg-[#0E5252] px-6 py-4">
            <h2 className={`text-2xl font-bold text-white ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('title')}
            </h2>
            <p className={`text-gray-200 mt-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Personal Information Section */}
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold text-gray-900 pb-2 border-b border-gray-200 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('sections.personal')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.name.label')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('fields.name.placeholder')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.admission_number.label')}
                  </label>
                  <input
                    type="text"
                    name="admission_number"
                    value={formData.admission_number}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('fields.admission_number.placeholder')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.gender.label')}
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="">{t('fields.gender.placeholder')}</option>
                    <option value="Male">{t('fields.gender.options.male')}</option>
                    <option value="Female">{t('fields.gender.options.female')}</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.date_of_birth.label')}
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.date_of_joining.label')}
                  </label>
                  <input
                    type="date"
                    name="date_of_joining"
                    value={formData.date_of_joining}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.address.label')}
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('fields.address.placeholder')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>
            </div>

            {/* Academic Information Section */}
            <div className="space-y-4">
              <h3 className={`text-lg font-semibold text-gray-900 pb-2 border-b border-gray-200 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('sections.academic')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.class.label')}
                  </label>
                  <select
                    name="class_id"
                    value={formData.class_id}
                    onChange={handleInputChange}
                    required
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="0">{t('fields.class.placeholder')}</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('fields.stream.label')}
                  </label>
                  <select
                    name="stream_id"
                    value={formData.stream_id}
                    onChange={handleInputChange}
                    required
                    disabled={!selectedClass || selectedClass.streams.length === 0}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${isRTL ? 'text-right' : 'text-left'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="0">
                      {!selectedClass ? t('fields.stream.select_class_first') : t('fields.stream.placeholder')}
                    </option>
                    {selectedClass?.streams.map(stream => (
                      <option key={stream.id} value={stream.id}>
                        {stream.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className={`flex pt-6 ${isRTL ? 'justify-start' : 'justify-end'}`}>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 bg-[#0E5252] text-white font-medium rounded-lg hover:bg-[#0A3D3D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0E5252] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <svg className={`animate-spin h-5 w-5 text-white ${isRTL ? 'ml-3 -mr-1' : '-ml-1 mr-3'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('buttons.submitting')}
                  </>
                ) : (
                  t('buttons.submit')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditStudent;